import React, { useMemo, useState } from 'react';
import { z } from 'zod';
import FormTemplate from '../../templates/FormTemplate';
import { Input } from '../../atoms/Input';
import { Select, SelectOptionType } from '../../atoms/Select';
import { Button } from '../../atoms/Button';
import styled from 'styled-components';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { COLOR_LIGHT_GRAY } from '../../../constants/color';
import { inquiryFormApi } from '../../../core/http/httpClient';
import { InquiryFormRequestBody } from '../../../core/api/request/home/inquiry-form/types';
import { useLanguage } from '../../../contexts/LanguageContext';

export const InquiryForm = () => {
  const { t } = useLanguage();

  const inquiryTypeOptions: SelectOptionType[] = [
    { id: 1, name: 'Rent', value: 'rent' },
    { id: 2, name: 'Sale', value: 'sale' },
    { id: 3, name: 'Purchase', value: 'purchase' },
  ];

  const roleOptions: SelectOptionType[] = [
    { id: 1, name: 'Client', value: 'client' },
    { id: 2, name: 'Property Owner', value: 'property_owner' },
    { id: 3, name: 'Real Estate Agency', value: 'real_estate_agency' },
  ];

  const [methods, setMethods] = useState<UseFormReturn<FieldValues, any, undefined> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const schema = useMemo(() =>
    z.object({
      inquiryType: z.string().min(1, t('forms.errors.inquiryTypeRequired')),
      role: z.string().min(1, t('forms.errors.roleRequired')),
      lastName: z.string().min(1, t('forms.errors.lastNameRequired')),
      firstName: z.string().min(1, t('forms.errors.firstNameRequired')),
      phoneNumber: z.string().min(1, t('forms.errors.phoneNumberRequired')),
      email: z.string().email(t('forms.errors.emailInvalid')),
      description: z.string().optional(),
    }),
    [t]);

  const handleSelectChange = (name: string, value: any) => {
    if (methods) {
      methods.setValue(name, value, { shouldValidate: true });
    }
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const requestBody: InquiryFormRequestBody = {
        inquiry_type: data.inquiryType,
        role: data.role,
        last_name: data.lastName,
        first_name: data.firstName,
        phone_number: data.phoneNumber,
        email_address: data.email,
        description: data.description || '',
      };

      await inquiryFormApi.call(requestBody);

      setSubmitMessage({
        type: 'success',
        message: t('forms.messages.inquirySuccess')
      });

      if (methods) {
        methods.reset();
      }
    } catch (error: any) {
      console.error('Error submitting inquiry:', error);
      setSubmitMessage({
        type: 'error',
        message: error.response?.data?.message || t('forms.messages.inquiryFailed')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <Title>{t('forms.inquiryForm.title')}</Title>

      <FormTemplate
        schema={schema}
        setMethods={setMethods as (method: UseFormReturn<FieldValues, any, undefined>) => void}
        defaultValues={{}}
        onSubmit={handleSubmit}
      >
        <FieldWrapper>
          <Select
            name="inquiryType"
            label={t('forms.fields.inquiryType')}
            placeholder={t('forms.placeholders.selectInquiryType')}
            options={inquiryTypeOptions}
            required
            onChange={handleSelectChange}
            width="100%"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Select
            name="role"
            label={t('forms.fields.role')}
            placeholder={t('forms.placeholders.selectRole')}
            options={roleOptions}
            required
            onChange={handleSelectChange}
            width="100%"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            name="lastName"
            label={t('forms.fields.lastName')}
            placeholder={t('forms.placeholders.lastName')}
            required
            width={"100%"}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            name="firstName"
            label={t('forms.fields.firstName')}
            placeholder={t('forms.placeholders.firstName')}
            required
            width={"100%"}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            name="phoneNumber"
            label={t('forms.fields.phoneNumber')}
            type="number"
            placeholder={t('forms.placeholders.phoneNumber')}
            required
            width={"100%"}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            name="email"
            label={t('forms.fields.email')}
            placeholder={t('forms.placeholders.email')}
            required
            type="email"
            width={"100%"}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            name="description"
            label={t('forms.fields.description')}
            placeholder={t('forms.placeholders.description')}
            type="textarea"
            required={false}
            width={"100%"}
          />
        </FieldWrapper>

        {submitMessage && (
          <MessageContainer $type={submitMessage.type}>
            {submitMessage.message}
          </MessageContainer>
        )}

        <ButtonWrapper>
          <Button
            text={isSubmitting ? t('forms.buttons.submitting') : t('forms.buttons.submit')}
            type="submit"
            backgroundColor="#06163A"
            textColor="#fff"
            width="140px"
            height="36px"
          />
        </ButtonWrapper>
      </FormTemplate>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 16px;
  background: #fff;
  border: 1px solid ${COLOR_LIGHT_GRAY};
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    margin: 20px auto;
    padding: 32px 24px;
  }
  
  @media (min-width: 1024px) {
    margin: 40px auto;
    padding: 40px 32px;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #06163A;
  margin-bottom: 16px;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 20px;
    text-align: left;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 24px;
  }
`;

const MessageContainer = styled.div<{ $type: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${({ $type }) => $type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${({ $type }) => $type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${({ $type }) => $type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const FieldWrapper = styled.div`
  margin-bottom: 18px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

export default InquiryForm;
